/**
 * Section definitions for the EOL care-plan review UI. Each section instance may
 * carry multiple `entries` in the API payload, but this UI always edits exactly
 * one entry row per section (see AddSectionModal initial state and save path).
 */
import {
  EolSectionType,
  type EolSectionTypeValue,
  placeOfCareOptions
} from './eolTypes'

export type FieldType = 'textarea' | 'richtext' | 'date' | 'select'

export interface EolFieldConfig {
  key: string
  label: string
  options?: { value: string; label: string }[]
  type: FieldType
}

export interface EolSectionConfigItem {
  color: string
  description: string
  fields: EolFieldConfig[]
  label: string
  maxSections: number
  sectionType: EolSectionTypeValue
}

const MAX_SECTIONS_SINGLE = 1
const MAX_SECTIONS_UNLIMITED = Infinity

type EolSectionConfigInput = Omit<EolSectionConfigItem, 'maxSections'> & {
  maxSections?: number
}

const buildSectionConfig = ({
  maxSections = MAX_SECTIONS_UNLIMITED,
  ...section
}: EolSectionConfigInput): EolSectionConfigItem => ({
  ...section,
  maxSections
})

export const eolSectionConfig: EolSectionConfigItem[] = [
  buildSectionConfig({
    sectionType: EolSectionType.custom,
    label: 'Custom',
    description: 'A custom section for additional information.',
    color: '#6366F1',
    fields: [{ key: 'description', type: 'richtext', label: 'Description' }]
  }),
  buildSectionConfig({
    sectionType: EolSectionType.anticipatoryActions,
    label: 'Anticipatory actions',
    description: 'Actions planned in anticipation of future care needs.',
    color: '#F59E0B',
    fields: [
      { key: 'description', type: 'textarea', label: 'Description' },
      {
        key: 'observationDate',
        type: 'date',
        label: 'Date of observation'
      }
    ]
  }),
  buildSectionConfig({
    sectionType: EolSectionType.anticipatoryMedicinesEquipment,
    label: 'Anticipatory medicines/equipment',
    description: 'Medicines and equipment prepared for anticipated needs.',
    color: '#3B82F6',
    fields: [
      { key: 'description', type: 'textarea', label: 'Description' },
      {
        key: 'observationDate',
        type: 'date',
        label: 'Date of observation'
      }
    ]
  }),
  buildSectionConfig({
    sectionType: EolSectionType.prognosis,
    label: 'Prognosis',
    description:
      'Prognosis records including awareness and estimated timelines.',
    color: '#1E40AF',
    fields: [
      {
        key: 'awarenessOfPrognosis',
        type: 'textarea',
        label: 'Awareness of prognosis'
      },
      { key: 'awarenessDate', type: 'date', label: 'Awareness date' },
      {
        key: 'estimatedPrognosis',
        type: 'textarea',
        label: 'Estimated prognosis'
      },
      {
        key: 'observationDate',
        type: 'date',
        label: 'Date of observation'
      }
    ]
  }),
  buildSectionConfig({
    sectionType: EolSectionType.instructions,
    label: 'Instructions',
    description: 'Detailed instructions for delivering care.',
    color: '#EC4899',
    maxSections: MAX_SECTIONS_SINGLE,
    fields: [
      {
        key: 'plannedActivities',
        type: 'textarea',
        label: 'Planned and scheduled activities'
      },
      {
        key: 'completedActions',
        type: 'textarea',
        label: 'Completed actions'
      },
      {
        key: 'howCareShouldBeProvided',
        type: 'textarea',
        label: 'How care should be provided'
      }
    ]
  }),
  buildSectionConfig({
    sectionType: EolSectionType.planOfContents,
    label: 'Plan of contents',
    description: 'Mandatory overview of this End of Life Care Plan.',
    color: '#06B6D4',
    fields: [{ key: 'details', type: 'textarea', label: 'Details' }]
  }),
  buildSectionConfig({
    sectionType: EolSectionType.preferredPlaceOfCare,
    label: 'Preferred place of care',
    description: "The person's preferred location to receive care.",
    color: '#22C55E',
    maxSections: MAX_SECTIONS_SINGLE,
    fields: [
      { key: 'description', type: 'textarea', label: 'Description' },
      {
        key: 'observationDate',
        type: 'date',
        label: 'Date of observation'
      },
      {
        key: 'snomedCode',
        type: 'select',
        label: 'Preferred place',
        options: placeOfCareOptions
      }
    ]
  }),
  buildSectionConfig({
    sectionType: EolSectionType.preferredPlaceOfDeath,
    label: 'Preferred place of death',
    description: "The person's preferred location at end of life.",
    color: '#8B5CF6',
    maxSections: MAX_SECTIONS_SINGLE,
    fields: [
      { key: 'description', type: 'textarea', label: 'Description' },
      {
        key: 'observationDate',
        type: 'date',
        label: 'Date of observation'
      },
      {
        key: 'snomedCode',
        type: 'select',
        label: 'Preferred place',
        options: placeOfCareOptions
      }
    ]
  }),
  buildSectionConfig({
    sectionType: EolSectionType.thisIsHowToSupportMe,
    label: 'This is how to support me',
    description: 'Personal preferences on how support should be provided.',
    color: '#F97316',
    maxSections: MAX_SECTIONS_SINGLE,
    fields: [{ key: 'description', type: 'textarea', label: 'Description' }]
  })
]
